// Server state точек и устройств.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { sessionKeys } from '@/shared/session';

import { locationsApi, type LocationPayload } from './api';

export const locationKeys = {
  all: ['locations'] as const,
  list: () => ['locations', 'list'] as const,
  devices: () => ['locations', 'devices'] as const,
  kioskBindings: (location?: string) => ['locations', 'kiosk-bindings', location ?? 'all'] as const,
} as const;

export function useLocations() {
  return useQuery({
    queryKey: locationKeys.list(),
    queryFn: () => locationsApi.list(),
  });
}

export function useDevices() {
  return useQuery({
    queryKey: locationKeys.devices(),
    queryFn: () => locationsApi.devices(),
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LocationPayload) => locationsApi.create(body),
    onSuccess: () => {
      // Инвалидируется весь домен, а не только список точек: новая точка приносит
      // с собой склонированное меню, и кэш каталога о нём ещё не знает.
      void queryClient.invalidateQueries({ queryKey: locationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export function useSaveLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<LocationPayload> }) =>
      locationsApi.update(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: locationKeys.all });
      // Настройки основной точки едут в профиле (зона, списание разом, время утра T-055),
      // и «Кухня» на этом же устройстве иначе узнала бы о правке только через пять
      // минут: админ поставил 09:00, а печать на утро ещё шла на 10:00 (пилот 2026-10-08).
      void queryClient.invalidateQueries({ queryKey: sessionKeys.me });
    },
  });
}

export function useCreateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { location: string; name: string }) => locationsApi.createDevice(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: locationKeys.devices() });
    },
  });
}

export function useKioskBindings(location?: string) {
  return useQuery({
    queryKey: locationKeys.kioskBindings(location),
    queryFn: () => locationsApi.kioskBindings(location),
  });
}

export function useBindKiosk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { location: string; name: string }) => locationsApi.bindKiosk(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locations', 'kiosk-bindings'] });
    },
  });
}

export function useRevokeKiosk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => locationsApi.revokeKiosk(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locations', 'kiosk-bindings'] });
    },
  });
}
